import {
  Users,
  Activity,
  Award,
  PieChart,
  MessageSquare,
  TrendingUp,
  Settings,
  Menu,
  X,
  BrainCircuit,
  LineChart,
  BriefcaseBusiness,
  Sparkles,
  Brain,
  LightbulbIcon,
  UserPlus,
  Ticket,
  BarChart2,
  LayoutDashboard,
  Smartphone,
  Settings2,
  User,
  Database,
  Apple,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import { useState, useEffect } from "react";
import { cn } from "@lib/utils";
import { Button } from "@components/ui/button";
import { useIsMobile } from "@hooks/useIsMobile";
import { SuperOneLogoText } from "./SuperOneLogoText";

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  isActive: boolean;
  onClick?: () => void;
}

function SidebarItem({
  icon,
  label,
  path,
  isActive,
  onClick,
}: SidebarItemProps) {
  return (
    <Link href={path}>
      <div
        className={cn(
          // Rectangle shape, full width, flush to left/right, no right border
          "flex items-center justify-start gap-4 px-4 py-2 rounded-none w-full border-l-4 transition-all cursor-pointer relative overflow-hidden text-[14px] font-medium border-r-0",
          isActive
            ? "bg-white/10 text-white border-l-[#0F87F2] border-l-4"
            : "text-gray-400 hover:text-white hover:bg-white/5 border-l-transparent border-l-4"
        )}
        style={{ borderLeftWidth: 4, borderRightWidth: 0 }}
        onClick={onClick}
      >
        <div
          className={cn(
            "w-5 h-5 flex items-center justify-center",
            isActive ? "text-white" : "text-gray-400"
          )}
        >
          {icon}
        </div>
        <span className="flex items-center">{label}</span>
        {/* Removed right border highlight */}
      </div>
    </Link>
  );
}

export function Sidebar() {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  const [logoFixed, setLogoFixed] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Effect to fix the z-index issue with the logo
  useEffect(() => {
    // Create a function to fix the logo layering by adding inline styles
    const fixLogoLayering = () => {
      const sidebarElement = document.querySelector('[data-sidebar="true"]');
      const logoElement = document.querySelector('[data-sidebar-logo="true"]');

      if (sidebarElement && logoElement && !logoFixed) {
        // Add styles to ensure proper layering
        (logoElement as HTMLElement).style.position = "relative";
        (logoElement as HTMLElement).style.zIndex = "9999";
        setLogoFixed(true);
      }
    };

    // Run initially and after a small delay to ensure DOM is fully loaded
    fixLogoLayering();
    const timeoutId = setTimeout(fixLogoLayering, 100);

    return () => clearTimeout(timeoutId);
  }, [logoFixed]);

  const toggleSidebar = () => setOpen(!open);
  const closeSidebar = () => setOpen(false);

  const navItems = [
    {
      label: "Overview",
      path: "/overview",
      icon: <LineChart className="h-4 w-4 mr-2" />,
    },
    {
      label: "Players",
      subItems: [
        {
          label: "Statistics",
          path: "/players/statistics",
          icon: <BarChart2 className="h-4 w-4 mr-2" />,
        },
        {
          label: "Activity & Engagement",
          path: "/players/activity-engagement",
          icon: <Activity className="h-4 w-4 mr-2" />,
        },
        {
          label: "Game Pass",
          path: "/players/game-pass",
          icon: <Ticket className="h-4 w-4 mr-2" />,
        },
        {
          label: "Sign Up",
          path: "/players/sign-up",
          icon: <UserPlus className="h-4 w-4 mr-2" />,
        },
        {
          label: "Artificial Intelligence",
          path: "/players/artificial-intelligence",
          icon: <LightbulbIcon className="h-4 w-4 mr-2" />,
        },
      ],
    },
    {
      label: "Apps",
      subItems: [
        {
          label: "AppTweak",
          path: "/apps/apptweak",
          icon: <LineChart className="h-4 w-4 mr-2" />,
        },
        {
          label: "Apple App Store",
          path: "/apps/apple-app-store",
          icon: <Apple className="h-4 w-4 mr-2" />,
        },
      ],
    },
    {
      label: "Settings",
      subItems: [
        {
          label: "General",
          path: "/settings/general",
          icon: <Settings2 className="h-4 w-4 mr-2" />,
        },
        {
          label: "Account",
          path: "/settings/account",
          icon: <User className="h-4 w-4 mr-2" />,
        },
        {
          label: "Data",
          path: "/settings/data",
          icon: <Database className="h-4 w-4 mr-2" />,
        },
      ],
    },
  ];

  return (
    <>
      {/* Mobile Toggle Button */}
      {isMobile && (
        <Button
          variant="outline"
          size="icon"
          className="fixed left-4 top-20 z-40 lg:hidden bg-gradient-to-r from-[#0F1625] to-[#0F2242] border-gray-800 text-white shadow-md"
          onClick={toggleSidebar}
        >
          <Menu className="h-4 w-4" />
          <span className="sr-only">Toggle sidebar</span>
        </Button>
      )}

      {/* Overlay for mobile */}
      {isMobile && open && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden backdrop-blur-sm"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 bottom-0 lg:left-0 z-50 w-64 border-r border-gray-800 bg-gradient-to-b from-[#0F1625] to-[#0F2242] transition-transform duration-300 ease-in-out",
          isMobile ? (open ? "left-0" : "-left-64") : "left-0"
        )}
      >
        {/* Mobile close button */}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2 lg:hidden text-white hover:bg-white/10 hover:text-white"
            onClick={closeSidebar}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close sidebar</span>
          </Button>
        )}

        {/* Logo with height aligned to header height */}
        <div
          data-sidebar="true"
          className="h-14 flex items-center justify-start px-6 border-b border-gray-800 relative z-[999]"
        >
          <div data-sidebar-logo="true" className="relative z-[999]">
            <SuperOneLogoText className="scale-110" />
          </div>
        </div>

        {/* Content area */}
        <div className="flex flex-col h-[calc(100%-3.5rem)] px-3 pt-1 pb-4">
          {/* Navigation */}
          <nav className="flex-1 space-y-2 overflow-y-auto mt-0">
            {navItems.map((item) => (
              <div key={item.label}>
                {/* Render item as a direct link if no subItems */}
                {item.subItems ? (
                  <>
                    <div className="flex items-center gap-4 px-4 py-2 text-[14px] font-semibold text-gray-300 select-none cursor-default">
                      {/* No icon for Players */}
                      <span className="flex items-center">{item.label}</span>
                    </div>
                    <div className="ml-3 mt-1 space-y-1">
                      {item.subItems.map((sub) =>
                        sub.path ? (
                          <Link key={sub.path} href={sub.path}>
                            <div
                              className={cn(
                                "flex items-center text-[14px] px-2 py-1 cursor-pointer transition-colors gap-2 border-l-4 rounded-none",
                                location === sub.path
                                  ? "bg-white/10 text-white font-semibold border-l-[#0F87F2]"
                                  : "text-gray-400 hover:text-white hover:bg-white/5 border-l-transparent hover:border-l-[#0F87F2]"
                              )}
                              style={{ borderLeftWidth: 4 }}
                            >
                              {sub.icon}
                              {sub.label}
                            </div>
                          </Link>
                        ) : null
                      )}
                    </div>
                  </>
                ) : (
                  <Link key={item.path} href={item.path}>
                    <div
                      className={cn(
                        "flex items-center text-[14px] px-2 py-1 cursor-pointer transition-colors gap-2 border-l-4 rounded-none",
                        location === item.path
                          ? "bg-white/10 text-white font-semibold border-l-[#0F87F2]"
                          : "text-gray-400 hover:text-white hover:bg-white/5 border-l-transparent hover:border-l-[#0F87F2]"
                      )}
                      style={{ borderLeftWidth: 4 }}
                    >
                      {item.icon}
                      {item.label}
                    </div>
                  </Link>
                )}
              </div>
            ))}

            {/* <div className="h-24" /> */}
          </nav>

          {/* Bottom section */}
          <div className="mt-auto px-4 py-3 border-t border-gray-800">
            <div className="flex items-center">
              <div className="h-7 w-7 rounded-full bg-gradient-to-br from-[#0F87F2]/30 to-[#4842F5]/30 flex items-center justify-center text-white mr-2">
                <span className="text-xs font-medium">A</span>
              </div>
              <div className="flex flex-col">
                <span className="text-xs font-medium text-white">
                  Admin User
                </span>
                <span className="text-[10px] text-gray-400">
                  admin@superone.com
                </span>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
